import { inject, Injectable } from '@angular/core';
import { FirebaseAnalytics } from '@capacitor-community/firebase-analytics';
import { firstValueFrom } from 'rxjs';
import { Logger } from 'src/app/core/helpers/logger';
import { isCapacitor } from 'src/app/core/helpers/utils';
import { MarketingData } from 'src/app/data/marketing.data';
import {
  ClassificationCode,
  HourlyServiceIds,
  IMarketingClassificationDto,
  IPaymentDetailsDto,
  MonthlyServiceIds,
  PaymentClassificationCodes,
  PENDING_WEB_PAYMENT,
} from '../contracts';
import { IDictionary } from '../core/contracts';
import { HourlyData } from '../data/hourly.data';
import { MonthlyData } from '../data/monthly.data';
import { AdjustEventTokens, AdjustService } from './adjust.service';
import { AnalyticsService } from './analytics.service';
import { getPaymentIdentifier, PaymentService } from './payment.service';

@Injectable({
  providedIn: 'root',
})
export class MarketingService {
  analytics = inject(AnalyticsService);
  private data = inject(MarketingData);
  private hourlyData = inject(HourlyData);
  private monthlyData = inject(MonthlyData);
  private logger = inject(Logger);
  adjust = inject(AdjustService);

  public async logPurchase(
    requestId: number,
    classificationCode: ClassificationCode,
    details: IPaymentDetailsDto,
    marketingClassification: IMarketingClassificationDto = null
  ) {
    if (!details) {
      this.logger.warn({ msg: '@marketing.service: Payment details missing, skipping purchase event log' });
      return;
    }

    if (details.amountToPay <= 0) {
      this.logger.warn({ msg: '@marketing.service: No amount to pay, request is probably activated without payment, skipping purchase event log' });
      return;
    }

    try {
      const identifier = getPaymentIdentifier(classificationCode, requestId);
      let serviceId: number | undefined = undefined;
      let serviceType: string | undefined = undefined;

      const amount = details.amountToPay ?? 0;
      const discount = details.discountAmount ?? 0;

      let itemName = PaymentService.isHourly(classificationCode)
        ? PaymentClassificationCodes.Khidma
        : PaymentService.isMonthly(classificationCode)
        ? PaymentClassificationCodes.Monthly
        : PaymentService.isInstallment(classificationCode)
        ? PaymentClassificationCodes.Installment
        : PaymentService.isDelayRequest(classificationCode)
        ? PaymentClassificationCodes.DelayRequest
        : 'Unknown';
      if (PaymentService.isHourly(classificationCode)) {
        try {
          const request = await firstValueFrom(this.hourlyData.getRequest(requestId));
          serviceId = request?.serviceId;
          serviceType = HourlyServiceIds[serviceId] || 'UnknownHourlyService';
          itemName = request.packageName || 'UnknownHourlyItem';
        } catch (err) {
          this.logger.error({ msg: '[MARKETING] Failed to get hourly request', err });
          serviceType = 'UnknownHourlyService';
          itemName = 'UnknownHourlyItem';
        }
      } else if (PaymentService.isMonthly(classificationCode)) {
        try {
          const request = await firstValueFrom(this.monthlyData.getRequestDetails(requestId));
          serviceId = request?.serviceId;
          serviceType = MonthlyServiceIds[serviceId] || 'UnknownMonthlyService';
          itemName = request?.requestSerial || 'UnknownMonthlyItem';
        } catch (err) {
          this.logger.error({ msg: '[MARKETING] Failed to get monthly request', err });
          serviceType = 'UnknownMonthlyService';
          itemName = 'UnknownMonthlyItem';
        }
      } else if (PaymentService.isInstallment(classificationCode)) {
        serviceType = 'Installment';
        itemName = 'Installment';
      } else if (PaymentService.isDelayRequest(classificationCode)) {
        serviceType = 'DelayRequest';
        itemName = 'DelayRequest';
      } else {
        serviceType = 'UnknownService';
        itemName = 'UnknownItem';
      }
      const eventParams: IDictionary<number | string> = {
        value: amount,
        currency: 'SAR',
        item_id: identifier,
        item_name: itemName,
        discount: discount,
        service_id: serviceId,
        service_type: serviceType,
      };
      // Firebase Analytics
      try {
        await FirebaseAnalytics.logEvent({
          name: 'purchase',
          params: eventParams,
        });
      } catch (err) {
        this.logger.error({ msg: '@marketing.service: FirebaseAnalytics purchase event FAILED', err });
      }

      // Adjust
      try {
        this.adjust.trackEvent(AdjustEventTokens.Purchase, { value: eventParams.value as number, currency: eventParams.currency as string });
      } catch (err) {
        this.logger.error({ msg: '@marketing.service: Adjust purchase event FAILED', err });
      }

      const classification = marketingClassification ?? (await this.data.getMarketingClassification());
      if (classification.isFirstTimeBuyer) {
        // First-time buyer: send on first successful purchase
        this.adjust.trackEvent(AdjustEventTokens.First_Time_Buyer, { order_id: identifier, user_cohort: 'first_time' });
        this.data.refreshClassification(); // refresh classification to update first-time buyer status for future events
      } else {
        // this doesn't match the "returning_user" event definition
        // but it's placed here because the order_id is one of the required parameters, and because the marketing team stated "returning user must have made a purchase before"
        this.adjust.trackEvent(AdjustEventTokens.Returning_User, { order_id: identifier, user_cohort: 'returning' });
      }
    } catch (err) {
      this.logger.error({ msg: '@marketing.service: Unknown error occurred while logging purchase event', err });
    }
  }

  /**
   * Handles marketing events for web payment returns.
   * On web, the SPA is destroyed during payment gateway redirect (window.open '_self').
   * This method checks sessionStorage for pending payment context saved before redirect,
   * fetches payment details, and fires logPurchase(). No-op if no pending context exists
   * (e.g. on native where postPaymentRedirect() already handled it in-memory).
   */
  async handleWebPaymentReturn(): Promise<void> {
    const raw = sessionStorage.getItem(PENDING_WEB_PAYMENT);
    if (!raw || isCapacitor) {
      return;
    }

    sessionStorage.removeItem(PENDING_WEB_PAYMENT);

    try {
      const { requestId, classificationCode, paymentDetails, marketingClassification } = JSON.parse(raw) as {
        requestId: number;
        classificationCode: ClassificationCode;
        paymentDetails?: IPaymentDetailsDto;
        marketingClassification: IMarketingClassificationDto;
      };

      // make sure to pass the original marketingClassification before the redirection on web, otherwise the latest value from the api will be used (where isFirstTimeBuyer after the payment will be false)
      await this.logPurchase(requestId, classificationCode, paymentDetails, marketingClassification);
    } catch (err) {
      this.logger.error({ msg: '[MARKETING] Web payment return event failed', err });
    }
  }
  async savePendingPaymentContext(requestId: number, classificationCode: ClassificationCode, paymentDetails: IPaymentDetailsDto) {
    const marketingClassification = await this.data.getMarketingClassification(); // make sure to save the isFirstTimeBuyer status before the redirection on web, otherwise the latest value from the api will be used (which is ofc after the payment will be false)
    sessionStorage.setItem(PENDING_WEB_PAYMENT, JSON.stringify({ requestId, classificationCode, paymentDetails, marketingClassification }));
  }
}
